import React from 'react';
import { Formik, useFormikContext } from 'formik';
import { useQuery } from '@tanstack/react-query';
import { TableHeader, TableHeaderCell, Form as SForm, Divider, Dropdown, Table, Input, Segment, FormGroup as SFormGroup, FormField as SFormField, TableRow, TableBody, TableCell } from '@/components/bp/bp-ui';
import { SEARCH_TYPES_NAMES } from '../../types';
import { DnaProfileSearchDto, MarkerAllelesSearchDto } from '@app/api2/api';
import { queriesClient } from '@app/api2/Clients';
import { DnaProfileSearchSchema } from './DnaForm.Schema';
import SemanticFormikInput from '@app/components/React/Common/Forms/SemanticFormikInput';
import { FormWithButtons } from './FormWithButtons';
import { ChangeObserver } from '@app/components/React/Labs/ChangeObserver';

interface Props {
  currentParams: DnaProfileSearchDto;
  setCurrentParams: (p: DnaProfileSearchDto) => void;
  onChange?: (params: DnaProfileSearchDto) => void;

}

function DnaFormFields() {
  const { values, setFieldValue } = useFormikContext<DnaProfileSearchDto>();
  const discriminator = SEARCH_TYPES_NAMES.DnaProfileSearchDto;

  const kits = useQuery({
    queryKey: ['allKits'],
    queryFn: async () => {
      return await queriesClient.getKits();
    }
  });

  const markers = useQuery({
    queryKey: ['kitMarkers', values.kitId],
    queryFn: async () => {
      return await queriesClient.getKitMarkers(values.kitId!);
    },
    enabled: !!values.kitId,
    // onSuccess: (data) => {
    //   const initialMarkers: Record<string, MarkerAllelesSearchDto> = {};
    //   data.systems.forEach(marker => {
    //     initialMarkers[marker.name] = { allele1: '', allele2: '', markerName: marker.name } as MarkerAllelesSearchDto;
    //   });
    //   setFieldValue('markers', { ...initialMarkers, ...(values.markers || {}) });
    // }
  });

  React.useEffect(() => {
    const initialMarkers: Record<string, MarkerAllelesSearchDto> = {};

    markers.data?.systems.forEach(marker => {
      initialMarkers[marker.name] = { allele1: '', allele2: '', markerName: marker.name } as MarkerAllelesSearchDto;
    });
    setFieldValue('markers', { ...initialMarkers, ...(values.markers || {}) });
  }, [markers.data])

  return (
    <>
      <SFormGroup widths="equal">
        <SemanticFormikInput
          name="andeSwabId"
          label="ANDE Swab ID"
          placeholder="Enter swab ID"
        />
        <SemanticFormikInput
          name="collectionDate"
          label="Collection Date"
          type="date"
        />
        <SemanticFormikInput
          name="collectedBy"
          label="Collected By"
          placeholder="Enter collector name"
        />
      </SFormGroup>
      <Divider horizontal>OR LOCI</Divider>

      <SFormField>
        <label>Loci Order *</label>
        <Dropdown
          placeholder="Kit"
          fluid
          selectOnNavigation={false}
          search
          selection
          value={values.kitId}
          options={kits.data?.map(kit => ({ key: `kit_${kit.id}`, text: kit.fullName, value: kit.id })) || []}
          onChange={(e, { value }) => {
            setFieldValue('kitId', value as number);
          }}
        />
      </SFormField>

      <Segment loading={markers.isLoading} style={{ marginTop: '1rem', maxHeight: '400px', overflowY: 'auto' }}>
        <Table celled compact>
          <TableHeader>
            <TableRow>
              <TableHeaderCell width={4}>System</TableHeaderCell>
              <TableHeaderCell width={6}>Allele 1</TableHeaderCell>
              <TableHeaderCell width={6}>Allele 2</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {values.markers && Object.keys(values.markers).map(locusName => (
              <TableRow key={locusName}>
                <TableCell><strong>{locusName}</strong></TableCell>
                <TableCell>
                  <Input
                    value={values.markers![locusName]?.allele1 || ''}
                    onChange={(e) => {
                      const newMarkers = { ...values.markers };
                      newMarkers[locusName] = { ...newMarkers[locusName], allele1: (e.target as HTMLInputElement).value };
                      setFieldValue('markers', newMarkers);
                    }}
                    transparent
                    fluid
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={values.markers![locusName]?.allele2 || ''}
                    onChange={(e) => {
                      const newMarkers = { ...values.markers };
                      newMarkers[locusName] = { ...newMarkers[locusName], allele2: (e.target as HTMLInputElement).value };
                      setFieldValue('markers', newMarkers);
                    }}
                    transparent
                    fluid
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Segment>
    </>
  );
}

export default function DnaForm({ currentParams, setCurrentParams, onChange }: Props) {
  const discriminator = SEARCH_TYPES_NAMES.DnaProfileSearchDto;

  const initialValues: DnaProfileSearchDto = {
    discriminator,
    operator: currentParams.operator,
    andeSwabId: currentParams.andeSwabId || '',
    collectionDate: currentParams.collectionDate,
    collectedBy: currentParams.collectedBy || '',
    kitId: currentParams.kitId,
    markers: currentParams.markers || {},
  };

  const handleSubmit = (values: DnaProfileSearchDto) => {
    setCurrentParams({ ...values, discriminator });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={DnaProfileSearchSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
    >
      {({ handleSubmit, values }) => (
        <>
          <ChangeObserver value={values} onChange={onChange} />
          <FormWithButtons handleSubmit={handleSubmit}>
            <DnaFormFields />
          </FormWithButtons>

        </>
      )}
    </Formik>
  );
}
